import { MapPin, Waves, Wind, ArrowUp, ChevronRight, Star, ThumbsDown, ThumbsUp } from 'lucide-react';
import { motion } from 'framer-motion';

export const API = process.env.REACT_APP_BACKEND_URL;

export const DIFFICULTY_COLORS = {
  beginner: 'bg-green-500/20 text-green-400',
  intermediate: 'bg-yellow-500/20 text-yellow-400',
  advanced: 'bg-orange-500/20 text-orange-400',
  expert: 'bg-red-500/20 text-red-400',
  all: 'bg-primary/20 text-primary',
};

export const WMO_CODES = {
  0: 'Clear', 1: 'Mostly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
  45: 'Fog', 48: 'Fog', 51: 'Light Drizzle', 53: 'Drizzle', 55: 'Heavy Drizzle',
  61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain', 80: 'Light Showers', 81: 'Showers',
  95: 'Thunderstorm', 96: 'Thunderstorm + Hail',
};

export const SHAPE_LABELS = [
  { max: 0.2, label: 'Blown Out', color: 'text-red-400 bg-red-400/10' },
  { max: 0.4, label: 'Poor', color: 'text-orange-400 bg-orange-400/10' },
  { max: 0.55, label: 'Fair', color: 'text-yellow-400 bg-yellow-400/10' },
  { max: 0.7, label: 'Fair-Good', color: 'text-lime-400 bg-lime-400/10' },
  { max: 0.85, label: 'Good', color: 'text-green-400 bg-green-400/10' },
  { max: 1.01, label: 'Epic', color: 'text-primary bg-primary/10' },
];

export function getShapeLabel(shape) {
  for (const s of SHAPE_LABELS) { if (shape <= s.max) return s; }
  return SHAPE_LABELS[0];
}

export function formatHour(h) {
  if (h === 0) return '12am';
  if (h < 12) return `${h}am`;
  if (h === 12) return '12pm';
  return `${h - 12}pm`;
}

export function formatTideTime(t) {
  if (!t || !t.includes(' ')) return t;
  const time = t.split(' ')[1];
  const [hh, mm] = time.split(':');
  const h = parseInt(hh, 10);
  if (h === 0) return `12:${mm}am`;
  if (h < 12) return `${h}:${mm}am`;
  if (h === 12) return `12:${mm}pm`;
  return `${h - 12}:${mm}pm`;
}

export function openDirections(lat, lon, name) {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const url = isIOS
    ? `maps://maps.apple.com/?daddr=${lat},${lon}&dirflg=d&t=m`
    : `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}&destination_place_id=&travelmode=driving`;
  window.open(url, '_blank');
}

export function ShakaIcon({ className = '' }) {
  return <ThumbsUp className={className} />;
}

export function DataSourceBadge({ label, active }) {
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-md font-medium ${active ? 'bg-green-500/15 text-green-400 border border-green-500/20' : 'bg-white/5 text-muted-foreground/40'}`}>
      {label}
    </span>
  );
}

export function LoadingDots({ color = 'bg-primary' }) {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="flex space-x-2">
        <div className={`w-2 h-2 ${color} rounded-full typing-dot`}></div>
        <div className={`w-2 h-2 ${color} rounded-full typing-dot`}></div>
        <div className={`w-2 h-2 ${color} rounded-full typing-dot`}></div>
      </div>
    </div>
  );
}

export function ReactionButtons({ spotName, myReaction, onReact }) {
  return (
    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <button
        data-testid={`shaka-${spotName.replace(/\s+/g, '-').toLowerCase()}`}
        onClick={() => onReact(spotName, myReaction === 'shaka' ? 'none' : 'shaka')}
        className={`p-1.5 rounded-lg transition-all ${myReaction === 'shaka' ? 'bg-primary/20 text-primary' : 'text-muted-foreground/30 hover:text-primary/60 hover:bg-white/5'}`}
        title="Shaka"
      >
        <ThumbsUp className="w-4 h-4" />
      </button>
      <button
        data-testid={`thumbsdown-${spotName.replace(/\s+/g, '-').toLowerCase()}`}
        onClick={() => onReact(spotName, myReaction === 'thumbsdown' ? 'none' : 'thumbsdown')}
        className={`p-1.5 rounded-lg transition-all ${myReaction === 'thumbsdown' ? 'bg-red-500/20 text-red-400' : 'text-muted-foreground/30 hover:text-red-400/60 hover:bg-white/5'}`}
        title="Thumbs down"
      >
        <ThumbsDown className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function SpotMeta({ lat, lon, name }) {
  if (lat == null || lon == null) return null;
  return (
    <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
      <span className="text-[9px] text-muted-foreground/40 font-mono">{lat.toFixed(2)}, {lon.toFixed(2)}</span>
      <button
        data-testid={`route-${name.replace(/\s+/g, '-').toLowerCase()}`}
        onClick={() => openDirections(lat, lon, name)}
        className="p-1.5 rounded-lg text-muted-foreground/40 hover:text-primary hover:bg-white/5 transition-all"
        title="Get directions"
      >
        <MapPin className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
